import { useState } from 'react';
import './ClarifyingQuestions.css';

interface QuestionOption {
  value: string;
  label: string;
  params: Record<string, any>;
  suggested?: boolean;
}

interface Question {
  id: string;
  question: string;
  type: 'select' | 'multiselect';
  options: QuestionOption[];
}

interface ClarifyingQuestionsProps {
  objectType: string | null;
  questions: Question[];
  defaults: Record<string, any>;
  message: string;
  onSubmit: (answers: Record<string, any>) => void;
  onSkip: () => void;
}

export const ClarifyingQuestions: React.FC<ClarifyingQuestionsProps> = ({
  questions,
  message,
  onSubmit,
  onSkip,
}) => {
  // Initialize answers with suggested options
  const [answers, setAnswers] = useState<Record<string, QuestionOption | QuestionOption[]>>(() => {
    const initial: Record<string, QuestionOption | QuestionOption[]> = {};
    questions.forEach(q => {
      if (q.type === 'multiselect') {
        // For multiselect, initialize with all suggested options
        initial[q.id] = q.options.filter(o => o.suggested);
      } else {
        // For select, initialize with first suggested option
        const suggested = q.options.find(o => o.suggested);
        if (suggested) {
          initial[q.id] = suggested;
        }
      }
    });
    return initial;
  });

  const handleSelectChange = (questionId: string, option: QuestionOption) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleMultiselectToggle = (questionId: string, option: QuestionOption) => {
    setAnswers(prev => {
      const current = (prev[questionId] as QuestionOption[]) || [];
      const isSelected = current.some(o => o.value === option.value);
      
      if (isSelected) {
        return {
          ...prev,
          [questionId]: current.filter(o => o.value !== option.value)
        };
      } else {
        return {
          ...prev,
          [questionId]: [...current, option]
        };
      }
    });
  };

  const handleSubmit = () => {
    // Convert answers to params format
    const formattedAnswers: Record<string, any> = {};
    
    Object.entries(answers).forEach(([questionId, answer]) => {
      if (Array.isArray(answer)) {
        // Multiselect - merge all params
        const mergedParams: Record<string, any> = {};
        answer.forEach(opt => {
          Object.assign(mergedParams, opt.params);
        });
        formattedAnswers[questionId] = { params: mergedParams };
      } else if (answer) {
        formattedAnswers[questionId] = { params: answer.params };
      }
    });
    
    onSubmit(formattedAnswers);
  };

  const isOptionSelected = (questionId: string, option: QuestionOption): boolean => {
    const answer = answers[questionId];
    if (Array.isArray(answer)) {
      return answer.some(o => o.value === option.value);
    }
    return (answer as QuestionOption)?.value === option.value;
  };

  return (
    <div className="clarifying-questions">
      <div className="cq-header">
        <div className="cq-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 01-1 1H9a1 1 0 01-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/>
          </svg>
        </div>
        <div className="cq-message">{message}</div>
      </div>
      
      <div className="cq-questions">
        {questions.map((question, qIndex) => (
          <div key={question.id} className="cq-question" style={{ animationDelay: `${qIndex * 0.1}s` }}>
            <div className="cq-question-text">{question.question}</div>
            <div className={`cq-options ${question.type === 'multiselect' ? 'multiselect' : ''}`}>
              {question.options.map((option) => (
                <button
                  key={option.value}
                  className={`cq-option ${isOptionSelected(question.id, option) ? 'selected' : ''} ${option.suggested ? 'suggested' : ''}`}
                  onClick={() => {
                    if (question.type === 'multiselect') {
                      handleMultiselectToggle(question.id, option);
                    } else {
                      handleSelectChange(question.id, option);
                    }
                  }}
                >
                  <span className="cq-option-label">{option.label}</span>
                  {option.suggested && !isOptionSelected(question.id, option) && (
                    <span className="cq-suggested-badge">suggested</span>
                  )}
                  {isOptionSelected(question.id, option) && (
                    <span className="cq-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="cq-actions">
        <button className="cq-submit" onClick={handleSubmit}>
          <span>Create with these settings</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="cq-skip" onClick={onSkip}>
          Use suggested defaults
        </button>
      </div>
    </div>
  );
};
